/**
 * Test ID utilities for consistent and stable data-testid attributes
 * Used for Playwright and E2E testing
 */

/**
 * Builds a hierarchical test ID string from parts
 * Filters out undefined/null values and joins with dots
 * Uses raw field names without transformation for predictability
 *
 * @param parts - Array of test ID segments (feature, section, field, part)
 * @returns Formatted test ID string in dot notation
 *
 */
export function buildTestId(...parts: (string | undefined | null)[]): string {
  return parts
    .filter((part): part is string => Boolean(part))
    .join('.');
}

/**
 * Splits a dot-separated field name into parts
 * Useful for converting react-hook-form nested field names to test ID segments
 * Uses raw field names without transformation for predictability
 *
 * @param fieldName - Field name from react-hook-form (can be nested with dots)
 * @returns Array of field name parts
 *
 */
export function parseFieldName(fieldName: string): string[] {
  return fieldName.split('.');
}

/**
 * Builds a test ID for a form field, handling nested react-hook-form names
 * Combines a form prefix with parsed field name parts and optional suffix
 *
 * @param formPrefix - Prefix identifying the form (e.g., 'strategy.basic-info')
 * @param fieldName - React Hook Form field name (e.g., 'name', 'contact.email')
 * @param suffix - Optional suffix for field parts (e.g., 'error', 'label')
 * @returns Complete test ID string
 *
 */
export function buildFieldTestId(
  formPrefix: string,
  fieldName: string,
  suffix?: string
): string {
  const fieldParts = parseFieldName(fieldName);
  return buildTestId(formPrefix, ...fieldParts, suffix);
}

/**
 * Builds a 4-part test ID for form fields following the standardized convention
 * Convention: {feature}.{page}.{section}.{field}[.{part}]
 *
 * @param feature - Feature name (e.g., 'signature-packet', 'deal-pipeline')
 * @param page - Page/step name (e.g., 'investor-details', 'basic-info')
 * @param section - Section within page (e.g., 'entity-info', 'tax-info')
 * @param field - Field name (preserved as-is from form definition)
 * @param part - Optional sub-component part (e.g., 'dropdown', 'option', 'calendar', 'error')
 * @returns Complete test ID string following 4-part convention
 *
 */
export function buildFormTestId(
  feature: string,
  page: string,
  section: string,
  field: string,
  part?: string
): string {
  return buildTestId(feature, page, section, field, part);
}
